/**
 * Page and file sharing for the open workspace: the SharingApi port, provided by the app shell next to WorkspaceContext
 * only where the feature is on. Absent → no Share buttons, no top bar sharing, no file sharing (as before). The hooks
 * share one view model per page / file / workspace among everything showing it. Native: an `@Environment` value /
 * `CompositionLocal` holding the same port.
 */
import {
  FileShareViewModel,
  PageShareViewModel,
  PublicPageViewModel,
  SharingSettingsViewModel,
  type FileShareState,
  type PageShareState,
  type PublicPageState,
  type SharingApi,
  type SharingSettingsState,
} from "@bundle/core";
import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import { useOwned } from "../hooks/owned.ts";
import { usePageTreeViewModel } from "../hooks/pages.ts";
import { useWorkspaceContext } from "./AppContext.tsx";

export const SharingContext = createContext<SharingApi | null>(null);

/** The workspace's SharingApi; null when sharing is off here. */
export function useSharingApi(): SharingApi | null {
  return useContext(SharingContext);
}

const noopSubscribe = () => () => {};
const none = (): null => null;

type Box<T> = { vm: T | null; dispose: () => void };

function useBoxState<S>(vm: { subscribe: (l: () => void) => () => void; getSnapshot: () => S } | null): S | null {
  return useSyncExternalStore(vm?.subscribe ?? noopSubscribe, vm?.getSnapshot ?? none, vm?.getSnapshot ?? none);
}

/** One page's sharing (top bar indicator + Share dialog); null when sharing is off. */
export function usePageShare(pageId: string): { vm: PageShareViewModel; state: PageShareState } | null {
  const api = useSharingApi();
  const { workspace } = useWorkspaceContext();
  const tree = usePageTreeViewModel();
  const key = `pageShare:${api === null ? "off" : workspace.wsId}:${workspace.state.meId}:${pageId}`;
  const create = useCallback((): Box<PageShareViewModel> => {
    const vm = api === null ? null : new PageShareViewModel(workspace, api, tree, pageId);
    return { vm, dispose: () => vm?.dispose() };
  }, [api, workspace, tree, pageId]);
  const vm = useOwned(key, create).vm;
  const state = useBoxState(vm);
  return vm === null || state === null ? null : { vm, state };
}

/** One file's Share dialog; null when sharing is off. */
export function useFileShare(fileId: string): { vm: FileShareViewModel; state: FileShareState } | null {
  const api = useSharingApi();
  const { workspace } = useWorkspaceContext();
  const key = `fileShare:${api === null ? "off" : workspace.wsId}:${workspace.state.meId}:${fileId}`;
  const create = useCallback((): Box<FileShareViewModel> => {
    const vm = api === null ? null : new FileShareViewModel(workspace, api, fileId);
    return { vm, dispose: () => vm?.dispose() };
  }, [api, workspace, fileId]);
  const vm = useOwned(key, create).vm;
  const state = useBoxState(vm);
  return vm === null || state === null ? null : { vm, state };
}

/** Settings → Workspace → Sharing; null when sharing is off. */
export function useSharingSettings(): { vm: SharingSettingsViewModel; state: SharingSettingsState } | null {
  const api = useSharingApi();
  const { workspace } = useWorkspaceContext();
  const key = `sharingSettings:${api === null ? "off" : workspace.wsId}:${workspace.state.meId}`;
  const create = useCallback((): Box<SharingSettingsViewModel> => {
    const vm = api === null ? null : new SharingSettingsViewModel(workspace, api);
    return { vm, dispose: () => vm?.dispose() };
  }, [api, workspace]);
  const vm = useOwned(key, create).vm;
  const state = useBoxState(vm);
  return vm === null || state === null ? null : { vm, state };
}

/** The signed-out /s/:token page (no workspace context: the caller passes the API). */
export function usePublicPage(api: SharingApi, token: string): { vm: PublicPageViewModel; state: PublicPageState } {
  const create = useCallback(() => new PublicPageViewModel(api, token), [api, token]);
  const vm = useOwned(`publicPage:${token}`, create);
  return { vm, state: useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot) };
}
